import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { empresaService } from '../../services/empresaService';
import { municipioService, ufService } from '../../services/enderecoService';
import { Option, UF } from '../../types/localidade';
import { toast } from 'react-toastify';

interface MudarPlanoModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
}

export const MudarPlanoModal: React.FC<MudarPlanoModalProps> = ({ isOpen, onClose, empresaId }) => {
  const [ufs, setUfs] = useState<UF[]>([]);
  const [selectedUf, setSelectedUf] = useState<Option | null>(null);
  const [cidades, setCidades] = useState<Option[]>([]);
  const [selectedCidade, setSelectedCidade] = useState<Option | null>(null);
  const [selectedSegmento, setSelectedSegmento] = useState<Option | null>(null);
  const [isSubmittingCidade, setIsSubmittingCidade] = useState(false);
  const [isSubmittingSegmento, setIsSubmittingSegmento] = useState(false);

  useEffect(() => {
    if (isOpen) {
      ufService.listar().then(setUfs);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedUf) {
      municipioService.listarPorUF(selectedUf.value).then(setCidades);
    } else {
      setCidades([]);
    }
  }, [selectedUf]);

  const handleAddCidade = async () => {
    if (!selectedCidade) {
      toast.error('Por favor, selecione uma cidade.');
      return;
    }
    setIsSubmittingCidade(true);
    try {
      await empresaService.adicionarCidade(empresaId, Number(selectedCidade.value));
      toast.success('Cidade adicionada com sucesso!');
      setSelectedUf(null);
      setSelectedCidade(null);
    } catch (error) {
      console.error('Erro ao adicionar cidade:', error);
      toast.error('Erro ao adicionar cidade. Verifique os limites do plano.');
    } finally {
      setIsSubmittingCidade(false);
    }
  };

  const handleAddSegmento = async () => {
    if (!selectedSegmento) {
      toast.error('Por favor, selecione um segmento.');
      return;
    }
    setIsSubmittingSegmento(true);
    try {
      await empresaService.adicionarSegmento(empresaId, selectedSegmento.value);
      toast.success('Segmento adicionado com sucesso!');
      setSelectedSegmento(null);
    } catch (error) {
      console.error('Erro ao adicionar segmento:', error);
      toast.error('Erro ao adicionar segmento. Verifique os limites do plano.');
    } finally {
      setIsSubmittingSegmento(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const ufOptions = ufs.map(uf => ({ value: String(uf.id), label: uf.sigla }));

  const segmentoOptions: Option[] = [
    { value: 'lote', label: 'Lote' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'compacto', label: 'Compacto' },
    { value: 'casa_apartamento', label: 'Casa e Apartamento' },
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-6 border w-full max-w-lg shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Adicionar Recursos ao Plano</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>
        <div className="space-y-6">
          {/* Adicionar Cidade */}
          <div className="p-4 border rounded-md space-y-4">
            <h4 className="font-semibold mb-2">Adicionar Cidade</h4>
            <Select
              options={ufOptions}
              value={selectedUf}
              onChange={setSelectedUf}
              placeholder="Selecione um estado"
              isClearable
            />
            <Select
              options={cidades}
              value={selectedCidade}
              onChange={setSelectedCidade}
              placeholder="Selecione uma cidade"
              isClearable
              isDisabled={!selectedUf}
            />
            <button
              onClick={handleAddCidade}
              disabled={isSubmittingCidade || !selectedCidade}
              className="mt-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {isSubmittingCidade ? 'Adicionando...' : 'Adicionar Cidade'}
            </button>
          </div>

          {/* Adicionar Segmento */}
          <div className="p-4 border rounded-md">
            <h4 className="font-semibold mb-2">Adicionar Segmento</h4>
            <Select
              options={segmentoOptions}
              value={selectedSegmento}
              onChange={setSelectedSegmento}
              placeholder="Selecione um segmento"
              isClearable
            />
            <button
              onClick={handleAddSegmento}
              disabled={isSubmittingSegmento || !selectedSegmento}
              className="mt-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {isSubmittingSegmento ? 'Adicionando...' : 'Adicionar Segmento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};